import React, { useState, useEffect } from 'react';
import { useSearchParams, useNavigate } from 'react-router-dom';
import { Lock, Eye, EyeOff, Check, AlertCircle, ArrowLeft } from 'lucide-react';
import axios from 'axios';
import Button from './ui/Button';
import Card from './ui/Card';

const ResetPassword = () => {
  const [searchParams] = useSearchParams();
  const navigate = useNavigate();
  const [formData, setFormData] = useState({
    newPassword: '',
    confirmPassword: ''
  });
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [success, setSuccess] = useState('');
  const [showPasswords, setShowPasswords] = useState({
    new: false,
    confirm: false
  });

  const token = searchParams.get('token');
  const API_BASE_URL = process.env.REACT_APP_BACKEND_URL || '';

  // Check if token is present
  useEffect(() => {
    if (!token) {
      setError('Invalid reset link. No token provided.');
    }
  }, [token]);

  const handleInputChange = (field, value) => {
    setFormData(prev => ({ ...prev, [field]: value }));
    if (error) setError('');
    if (success) setSuccess('');
  };

  const togglePasswordVisibility = (field) => {
    setShowPasswords(prev => ({ ...prev, [field]: !prev[field] }));
  };

  const validateForm = () => {
    if (!formData.newPassword) {
      setError('New password is required');
      return false;
    }
    if (formData.newPassword.length < 6) {
      setError('Password must be at least 6 characters long');
      return false;
    }
    if (formData.newPassword !== formData.confirmPassword) {
      setError('Passwords do not match');
      return false;
    }
    return true;
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    if (!token) {
      setError('Invalid reset link. Please request a new password reset.');
      return;
    }

    if (!validateForm()) return;

    setLoading(true);
    setError('');
    setSuccess('');

    try {
      await axios.post(`${API_BASE_URL}/api/auth/reset-password`, {
        token: token,
        new_password: formData.newPassword
      });

      setSuccess('Password reset successful! Redirecting to login...');
      
      // Redirect to login page after 3 seconds
      setTimeout(() => {
        navigate('/');
      }, 3000);

    } catch (error) {
      console.error('Reset password error:', error);
      const errorMessage = error.response?.data?.detail || 'Failed to reset password. Please try again.';
      setError(errorMessage);
    } finally {
      setLoading(false);
    }
  };

  const handleBackToLogin = () => {
    navigate('/');
  };

  return (
    <div style={{
      minHeight: '100vh',
      background: 'var(--gradient-brand)',
      display: 'flex',
      alignItems: 'center',
      justifyContent: 'center',
      padding: '20px'
    }}>
      <Card style={{ 
        maxWidth: '500px', 
        width: '100%',
        textAlign: 'center'
      }}>
        <Card.Content style={{ padding: '48px 32px' }}>
          <div style={{
            width: '80px',
            height: '80px',
            margin: '0 auto 24px',
            background: success 
              ? 'var(--gradient-success)'
              : error
              ? 'var(--gradient-danger)'
              : 'var(--gradient-warning)',
            borderRadius: '50%',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center'
          }}>
            {success ? (
              <Check size={40} color="white" />
            ) : error ? (
              <AlertCircle size={40} color="white" />
            ) : (
              <Lock size={40} color="white" />
            )}
          </div>
          
          <h1 style={{
            fontSize: '24px',
            fontWeight: '700',
            color: 'var(--color-text-primary)',
            margin: '0 0 12px 0'
          }}>
            {success ? 'Password Reset Complete!' : error ? 'Reset Failed' : 'Reset Your Password'}
          </h1>
          
          <p style={{
            color: 'var(--color-text-muted)',
            fontSize: '16px',
            margin: '0 0 24px 0',
            lineHeight: '1.5'
          }}>
            {success 
              ? 'Your password has been successfully updated. You can now login with your new password.'
              : error
              ? 'There was an issue resetting your password. Please try again or request a new reset link.'
              : 'Enter your new password below to complete the reset process.'
            }
          </p>

          {!success && !error && (
            <form onSubmit={handleSubmit} style={{ textAlign: 'left' }}>
              {/* New Password */}
              <div style={{ marginBottom: '20px' }}>
                <label style={{
                  display: 'block',
                  fontSize: '14px',
                  fontWeight: '500',
                  color: 'var(--color-text-secondary)',
                  marginBottom: '6px'
                }}>
                  New Password
                </label>
                <div style={{ position: 'relative' }}>
                  <input
                    type={showPasswords.new ? 'text' : 'password'}
                    value={formData.newPassword}
                    onChange={(e) => handleInputChange('newPassword', e.target.value)}
                    style={{
                      width: '100%',
                      padding: '12px 40px 12px 12px',
                      border: '1px solid var(--color-border-strong)',
                      borderRadius: '6px',
                      fontSize: '14px',
                      outline: 'none',
                      transition: 'border-color 0.15s ease'
                    }}
                    onFocus={(e) => e.target.style.borderColor = 'var(--color-warning)'}
                    onBlur={(e) => e.target.style.borderColor = 'var(--color-border-strong)'}
                    placeholder="Enter new password (min 6 characters)"
                    required
                  />
                  <button
                    type="button"
                    onClick={() => togglePasswordVisibility('new')}
                    style={{
                      position: 'absolute',
                      right: '12px',
                      top: '50%',
                      transform: 'translateY(-50%)',
                      background: 'none',
                      border: 'none',
                      cursor: 'pointer',
                      color: 'var(--color-text-muted)',
                      padding: '2px'
                    }}
                  >
                    {showPasswords.new ? <EyeOff size={16} /> : <Eye size={16} />}
                  </button>
                </div>
              </div>

              {/* Confirm Password */}
              <div style={{ marginBottom: '24px' }}>
                <label style={{
                  display: 'block',
                  fontSize: '14px',
                  fontWeight: '500',
                  color: 'var(--color-text-secondary)',
                  marginBottom: '6px'
                }}>
                  Confirm New Password
                </label>
                <div style={{ position: 'relative' }}>
                  <input
                    type={showPasswords.confirm ? 'text' : 'password'}
                    value={formData.confirmPassword}
                    onChange={(e) => handleInputChange('confirmPassword', e.target.value)}
                    style={{
                      width: '100%',
                      padding: '12px 40px 12px 12px',
                      border: '1px solid var(--color-border-strong)',
                      borderRadius: '6px',
                      fontSize: '14px',
                      outline: 'none',
                      transition: 'border-color 0.15s ease'
                    }}
                    onFocus={(e) => e.target.style.borderColor = 'var(--color-warning)'}
                    onBlur={(e) => e.target.style.borderColor = 'var(--color-border-strong)'}
                    placeholder="Confirm your new password"
                    required
                  />
                  <button
                    type="button"
                    onClick={() => togglePasswordVisibility('confirm')}
                    style={{
                      position: 'absolute',
                      right: '12px',
                      top: '50%',
                      transform: 'translateY(-50%)',
                      background: 'none',
                      border: 'none',
                      cursor: 'pointer',
                      color: 'var(--color-text-muted)',
                      padding: '2px'
                    }}
                  >
                    {showPasswords.confirm ? <EyeOff size={16} /> : <Eye size={16} />}
                  </button>
                </div>
              </div>

              <Button
                type="submit"
                loading={loading}
                disabled={loading || !token}
                style={{
                  width: '100%',
                  background: 'var(--gradient-warning)',
                  color: 'white',
                  border: 'none',
                  padding: '12px',
                  fontSize: '14px',
                  fontWeight: '600'
                }}
              >
                {loading ? 'Resetting Password...' : 'Reset Password'}
              </Button>
            </form>
          )}

          {/* Token Info */}
          {token && (
            <div style={{
              background: '#f0f9ff',
              border: '1px solid var(--color-info-border)',
              borderRadius: '6px',
              padding: '12px',
              margin: '20px 0',
              fontSize: '12px',
              color: 'var(--color-info-fg)'
            }}>
              <strong>Reset Token:</strong> {token.slice(0, 8)}...{token.slice(-8)}
            </div>
          )}

          {/* Error Message */}
          {error && (
            <div style={{
              background: 'var(--color-danger-bg)',
              border: '1px solid var(--color-danger-border)',
              color: 'var(--color-danger-hover)',
              padding: '12px',
              borderRadius: '6px',
              fontSize: '14px',
              margin: '20px 0',
              textAlign: 'left'
            }}>
              <div style={{ display: 'flex', alignItems: 'center', gap: '8px', marginBottom: '4px' }}>
                <AlertCircle size={16} />
                <strong>Error</strong>
              </div>
              {error}
            </div>
          )}

          {/* Success Message */}
          {success && (
            <div style={{
              background: 'var(--color-success-bg)',
              border: '1px solid var(--color-success-border)',
              color: 'var(--color-success-fg)',
              padding: '12px',
              borderRadius: '6px',
              fontSize: '14px',
              margin: '20px 0',
              textAlign: 'left'
            }}>
              <div style={{ display: 'flex', alignItems: 'center', gap: '8px', marginBottom: '4px' }}>
                <Check size={16} />
                <strong>Success!</strong>
              </div>
              {success}
            </div>
          )}

          {/* Back to Login Button */}
          <div style={{ marginTop: '24px' }}>
            <Button
              onClick={handleBackToLogin}
              variant="secondary"
              icon={<ArrowLeft size={16} />}
              style={{
                background: 'transparent',
                color: 'var(--color-text-muted)',
                border: '1px solid var(--color-border-strong)'
              }}
            >
              Back to Login
            </Button>
          </div>

          {success && (
            <p style={{
              fontSize: '12px',
              color: 'var(--color-text-disabled)',
              margin: '16px 0 0 0'
            }}>
              Redirecting automatically in 3 seconds...
            </p>
          )}
        </Card.Content>
      </Card>
    </div>
  );
};

export default ResetPassword;